import { useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Ticket, Loader2, Check } from "lucide-react";

/**
 * RedeemCodeDialog — accepts a coupon code and applies it via
 * POST /api/studio/redeem-coupon. Handles bonus_credits, edu_grant
 * (and acknowledges percent_off for future Stripe checkout).
 */
export default function RedeemCodeDialog({ open, onOpenChange, onRedeemed }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const reset = () => { setCode(""); setSuccess(null); setSubmitting(false); };

  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return toast.error("Enter a code.");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/studio/redeem-coupon`, { code: trimmed });
      setSuccess(r.data);
      toast.success(r.data.message || "Code redeemed.");
      onRedeemed?.(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not redeem code.";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid="redeem-code-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] w-[94vw] max-w-[460px] sm:!rounded-2xl"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/8 space-y-1">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Ticket className="w-3 h-3" /> Wallet · Redeem
          </div>
          <DialogTitle className="font-display text-lg md:text-xl tracking-tight text-white">
            Redeem a code
          </DialogTitle>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            Got a student / institutional code or a launch promo? Drop it here to add credits or unlock Edu access.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
            placeholder="e.g. NIFT2026"
            maxLength={40}
            autoFocus
            data-testid="redeem-code-input"
            className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 tracking-[0.12em] uppercase focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
          />

          {success && (
            <div className="rounded-lg border border-[#D4AF37]/40 bg-[#1A1814] p-3 flex items-start gap-2">
              <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div className="text-[12px] text-white/80 leading-relaxed">
                {success.message}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              {success ? "Close" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !code.trim()}
              data-testid="redeem-code-submit"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-xs font-medium py-2 hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redeeming…</>
              ) : (
                <><Ticket className="w-3.5 h-3.5" /> Apply code</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
